import { MessageSquare, MessageCircleReply, CalendarDays } from "lucide-react";
import HoverCard from "./HoverCard";
import { formatDate } from "./crmUi";
import type { RfpPrimeContact } from "../api/CrmApi";

interface Props {
  contact: RfpPrimeContact;
  onClick: () => void;
}

// Same custom hover-card chrome as HighValueTooltip (via HoverCard), for
// the reply/log-a-message icon — replaces the plain `title=` tooltip that
// could only show one flat, unstyled line of the reply text.
export default function ReplyTooltip({ contact, onClick }: Props) {
  const replied = !!contact.replied_at;
  // replied_at is a naive-UTC *_at timestamp — utc=true shifts it to the
  // viewer's local time (see crmUi.formatDate's own docstring).
  const repliedDate = contact.replied_at ? formatDate(contact.replied_at, true) : null;

  return (
    <HoverCard
      accent={replied ? "border-success-border" : "border-border"}
      trigger={
        <button onClick={onClick} className="flex-shrink-0">
          <MessageSquare
            className={`w-3.5 h-3.5 transition-colors ${
              replied ? "text-success-soft-foreground fill-success/20" : "text-muted-foreground/40 hover:text-success-soft-foreground"
            }`}
          />
        </button>
      }
    >
      {!replied ? (
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
          Log a reply
        </span>
      ) : (
        <span className="flex flex-col gap-1.5">
          <span className="flex items-center gap-1.5">
            <MessageCircleReply className="w-3.5 h-3.5 text-success-soft-foreground flex-shrink-0" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-success-soft-foreground">Replied</span>
          </span>
          {contact.reply_text && (
            <span className="text-xs text-foreground leading-snug max-h-24 overflow-y-auto">
              {contact.reply_text}
            </span>
          )}
          <span className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground border-t border-border pt-1.5 mt-0.5">
            <span className="flex items-center gap-1">
              {repliedDate && (
                <>
                  <CalendarDays className="w-3 h-3" /> {repliedDate}
                </>
              )}
            </span>
            <span className="italic">Click for full conversation</span>
          </span>
        </span>
      )}
    </HoverCard>
  );
}
